import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useDelayCall, useDidUpdate } from '@equifinance/shared';
import {
  UploadTaskDocumentPayload,
  Tasks,
  defaultTasks,
  useTasksContext,
  PostWrittenEvidencePayload,
  PostTaskNotePayload,
  TaskType,
  UnderwriterDecisionPayload,
} from '.';
import useTasksApi from '../../api/useTasksApi';
import { useAccountContext } from '../account';

const extractTaskIndex = (path: string): number | null => {
  const match = path.match(/^tasks\[(\d+)\]\./);
  return match ? Number(match[1]) : null;
};

export function useTasks(id: string) {
  const [tasks, setTasks] = useState<Tasks>(defaultTasks);
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const { fetchTransitionStatus } = useAccountContext();

  const incrementedUpdateCount = (prevUpdateCount: number) => (prevUpdateCount ? prevUpdateCount + 1 : 1);

  const dirtyTaskIdsRef = useRef<Set<string>>(new Set());

  const updateTasks = async (field: string, value: any, disableIncrementUpdateCount: boolean = false) => {
    // Tracking dirty task from field.
    const taskIndex = extractTaskIndex(field);

    if (taskIndex != null && !disableIncrementUpdateCount) {
      const taskId = tasks.tasks[taskIndex].id;
      dirtyTaskIdsRef.current?.add(taskId);
    }

    setTasks((prev) => {
      const clone = _.cloneDeep(prev);
      const newTasks = _.set(clone, field, value);
      return {
        ...newTasks,
        updateCount: (disableIncrementUpdateCount ? prev?.updateCount : incrementedUpdateCount(prev?.updateCount)),
        dirtyCount: incrementedUpdateCount(prev?.dirtyCount),
      };
    });
  };

  const {
    getTaskTypes,
    getTasks,
    uploadDocuments,
    postTaskNote,
    postTask,
    postWrittenEvidence,
    postReadTask,
    postCancelTask,
    postReturnToBroker,
    postSubmitToLender,
    postUnderwriterSubmission,
  } = useTasksApi();

  const fetchTaskTypes = async () => {
    const { taskTypes } = await getTaskTypes(id);
    setTaskTypes(taskTypes ?? []);
  };

  const fetchTasks = async () => {
    const result = await getTasks(id);
    setTasks((prev) => ({
      ...prev,
      ...result,
    }));
  };

  const createTask = async () => {
    const result = await postTask(id);
    setTasks((prev) => ({
      ...prev,
      ...result,
    }));

    fetchTransitionStatus();
  };

  const uploadTaskDocuments = async (documents: UploadTaskDocumentPayload): Promise<Tasks> => {
    const result = await uploadDocuments(id, documents);
    setTasks((prev) => ({
      ...prev,
      ...result,
    }));
    fetchTransitionStatus();

    return result;
  };

  const createWrittenEvidence = async (writtenEvidence: PostWrittenEvidencePayload): Promise<Tasks> => {
    const result = await postWrittenEvidence(id, writtenEvidence);
    setTasks((prev) => ({
      ...prev,
      ...result,
    }));

    return result;
  };

  const postNote = async (note: PostTaskNotePayload): Promise<Tasks> => {
    const result = await postTaskNote(id, note);
    setTasks((prev) => ({
      ...prev,
      ...result,
    }));

    return result;
  };

  const readTask = async (taskId: string) => {
    try {
      await postReadTask(id, taskId);
      setTasks((prev) => {
        const updatedTask = prev.tasks.find((task) => task.id === taskId);
        if (updatedTask) updatedTask.updated = false;
        return {
          ...prev,
        };
      });
      return true;
    } catch {
      return false;
    }
  };

  const cancelTask = async (taskId: string) => {
    const result = await postCancelTask(id, taskId);
    setTasks((prev) => ({
      ...prev,
      ...result,
    }));
    return result;
  };

  const unklockTask = async (taskId: string) => {
    const result = await postReturnToBroker(id, taskId);
    setTasks((prev) => ({
      ...prev,
      ...result,
    }));
    return result;
  };

  const submitToLender = async (taskId: string) => {
    const result = await postSubmitToLender(id, taskId);
    setTasks((prev) => ({
      ...prev,
      ...result,
    }));
  };

  const underwriterSubmission = async (underwriterDecision: UnderwriterDecisionPayload) => {
    const result = await postUnderwriterSubmission(id, underwriterDecision);
    setTasks((prev) => ({
      ...prev,
      ...result,
    }));
  };

  useEffect(() => {
    (async () => {
      await fetchTasks();
      await fetchTaskTypes();
    })();
  }, [id]);

  return {
    tasks,
    taskTypes,
    setTasksData: setTasks,
    dirtyTaskIdsRef,
    updateTasks,
    fetchTasks,
    uploadTaskDocuments,
    createWrittenEvidence,
    postNote,
    createTask,
    readTask,
    cancelTask,
    unklockTask,
    submitToLender,
    underwriterSubmission,
  };
}

export const useTasksAutoSave = () => {
  const didUpdate = useDidUpdate();
  const {
    id, tasks, setTasksData, dirtyTaskIdsRef,
  } = useTasksContext();
  const { fetchTransitionStatus } = useAccountContext();

  const { putTasks } = useTasksApi();

  useDelayCall(() => {
    if (!didUpdate) return;
    (async () => {
      const { updateCount, dirtyCount } = tasks;

      // snapshot ids to avoid losing new edits during in-flight request.
      const dirtyIdsSnapshot = new Set(dirtyTaskIdsRef.current);

      const dirtyTasks = tasks.tasks.filter(({ id }) => dirtyIdsSnapshot.has(id));
      const newTasks = await putTasks(id, { ...tasks, ...{ tasks: dirtyTasks } });

      dirtyIdsSnapshot.forEach((taskId) => dirtyTaskIdsRef.current.delete(taskId));

      fetchTransitionStatus();

      setTasksData((prev: Tasks) => {
        if (updateCount >= prev?.updateCount && dirtyCount >= prev?.dirtyCount) {
          return {
            ...newTasks,
            updateCount,
          };
        }
        return prev;
      });
    })();
  }, [tasks?.updateCount]);
};
